/** @jsxImportSource theme-ui @jsxRuntime classic */
import {
  AnchorSection,
  Box,
  Button,
  Flex,
  Link,
  SanityRichText,
  Section,
  IntrinsicImage,
} from '@tishman/components';
import * as Icons from '@tishman/icons';
import { H } from '@hzdg/sectioning';
import { graphql, navigate } from 'gatsby';
import invariant from 'invariant';
import React, { useEffect, useRef, useState } from 'react';
import { animated, useTransition } from '@react-spring/web';
import { IGatsbyImageData } from 'gatsby-plugin-image';

import { Layout } from '../../layouts';

import type { Block } from '@sanity/block-content-to-react';
import type { PageConfig } from '../../PageConfig';

export const pageConfig: PageConfig = {
  theme: 'Rock Center',
  logo: 'Rockefeller Center',
  pageName: 'Leasing',
  cta: {
    to: '/leasing/#inquire',
    label: 'Inquire',
  },
};

export const query = graphql`
  query LeasingDetail($id: String!) {
    detailPage: sanityLeasingDetailPage(_id: { eq: "leasingDetailPage" }) {
      copy: _rawCopy
      cta {
        caption
        url
      }
      title
    }
    landingPage: sanityLeasingLandingPage(_id: { eq: "leasingLandingPage" }) {
      properties {
        _key
        property {
          _id
          slug {
            current
          }
          title
        }
      }
    }
    meta: sanityLeasingProperty(id: { eq: $id }) {
      seo {
        description: metaDescription
        title: metaTitle
        ogImage: metaImage {
          asset {
            gatsbyImageData(
              layout: FULL_WIDTH
              placeholder: BLURRED
              formats: [AUTO, WEBP]
            )
          }
        }
      }
    }
    property: sanityLeasingProperty(id: { eq: $id }) {
      attributesSection {
        attributes {
          _key
          title
          value: _rawValue
        }
        title
      }
      copy: _rawCopy
      ctas {
        _key
        caption
        url
      }
      images {
        _key
        alt
        asset {
          gatsbyImageData(width: 1600, layout: CONSTRAINED)
        }
      }
      slug {
        current
      }
      thumbnails: images {
        _key
        alt
        asset {
          gatsbyImageData(width: 1600, layout: CONSTRAINED)
        }
      }
      title
    }
  }
`;

const LeasingDetail: React.FC<{
  data: Queries.LeasingDetailQuery;
}> = ({ data: { detailPage, landingPage, property } }) => {
  invariant(detailPage, 'Expected leasing detail page data');
  invariant(landingPage, 'Expected leasing landing page data');
  invariant(property, 'Expected leasing property data');

  const properties = landingPage.properties?.map((prop) => prop?.property);

  const [currentImage, setCurrentImage] = useState(0);
  const transitions = useTransition(currentImage, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const attributeRefs = useRef(new Map<string, HTMLDivElement | null>());
  const [borderlessAttributes, setBorderlessAttributes] = useState(
    new Set<string>(),
  );

  const updateBorderlessAttributes = () => {
    const keys = [...attributeRefs.current.keys()].filter(
      (key) => attributeRefs.current.get(key) !== null,
    );
    const rects = keys.map((key) =>
      attributeRefs.current.get(key)!.getBoundingClientRect(),
    );

    if (rects.length === 0) {
      setBorderlessAttributes(new Set());
      return;
    }

    const leftMostPoint = Math.min(...rects.map((rect) => rect.left));
    const bottomLeftMostRect = rects.reduce(
      (prev, curr) => {
        return curr.left === leftMostPoint && curr.bottom >= prev.bottom
          ? curr
          : prev;
      },
      { bottom: 0, left: Infinity } as DOMRect,
    );

    const rightMostPoint = Math.max(...rects.map((rect) => rect.right));
    const bottomRightMostRect = rects.reduce(
      (prev, curr) => {
        return curr.right === rightMostPoint && curr.bottom >= prev.bottom
          ? curr
          : prev;
      },
      { bottom: 0, right: 0 } as DOMRect,
    );

    setBorderlessAttributes(
      new Set([
        keys[rects.indexOf(bottomLeftMostRect)],
        keys[rects.indexOf(bottomRightMostRect)],
      ]),
    );
  };

  useEffect(updateBorderlessAttributes, [
    property.attributesSection?.attributes,
  ]);

  useEffect(() => {
    window.addEventListener('resize', updateBorderlessAttributes);
    return () =>
      window.removeEventListener('resize', updateBorderlessAttributes);
  }, []);

  return (
    <Layout>
      <Box
        sx={{
          maxWidth: 1280,
          mb: [5, null, 8],
          mt: [5, null, 7],
          mx: 'auto',
          px: [3, null, 9],
        }}
      >
        <Flex
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexDirection: ['column', null, 'row'],
            gap: [3, null, null],
          }}
        >
          <Flex
            sx={{
              alignItems: 'flex-start',
              flexDirection: 'column',
              gap: [3, null, 4],
              maxWidth: 600,
            }}
          >
            <H sx={{ variant: 'text.mediumTitle', fontSize: [6, null, 7] }}>
              {detailPage.title}
            </H>
            <SanityRichText
              blocks={detailPage.copy as unknown as Block[]}
              sx={{ 'variant': 'text.mediumP', 'p:last-child': { mb: 0 } }}
            ></SanityRichText>
            <Link to=".." variant="underline">
              See All Properties
            </Link>
          </Flex>
          {!!detailPage.cta?.url && (
            <Link
              sx={{ px: 14, py: 12, whiteSpace: 'nowrap' }}
              to={detailPage.cta.url.replace(
                '{slug}',
                property.slug?.current || '',
              )}
              variant="button"
            >
              {detailPage.cta.caption}
            </Link>
          )}
        </Flex>
      </Box>
      <Section
        data-section="LeasingBuildings"
        id="buildings"
        sx={{
          maxWidth: 1280,
          mb: [5, null, 8],
          mt: [5, null, 7],
          mx: 'auto',
          px: [3, null, 7],
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: [4, null, 6],
          }}
        >
          {/* BUILDING SELECTOR */}
          <Flex
            sx={{
              alignItems: 'flex-start',
              flexDirection: 'column',
              gap: [3, null, 4],
            }}
          >
            <Box sx={{ position: 'relative' }}>
              {/* eslint-disable-next-line jsx-a11y/no-onchange */}
              <select
                onChange={(e) => {
                  navigate(`../${e.target.value}`);
                }}
                style={{
                  cursor: 'pointer',
                  height: '100%',
                  left: 0,
                  position: 'absolute',
                  top: 0,
                  width: '100%',
                }}
                value={property.slug?.current ?? ''}
              >
                {properties?.map((property) => (
                  <option
                    key={property?._id}
                    value={property?.slug?.current ?? ''}
                  >
                    {property?.title}
                  </option>
                ))}
              </select>
              <H
                sx={{
                  'variant': 'text.mediumTitle',
                  'backgroundColor': 'background',
                  'fontSize': [6, null, 7],
                  'pointerEvents': 'none',
                  'position': 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    backgroundColor: 'text',
                    height: '2px',
                    display: 'block',
                    left: 0,
                    bottom: 0,
                    right: 0,
                  },
                }}
              >
                <span>{property.title}</span>
                <Icons.DropArrow
                  style={{
                    height: 20,
                    marginLeft: '16px',
                    verticalAlign: 'middle',
                    width: 20,
                  }}
                />
              </H>
            </Box>
          </Flex>
          <Flex sx={{ flexWrap: 'wrap', gap: [3, null, 4] }}>
            <Box
              as="div"
              sx={{
                'flex': ['0 0 auto', '0 1 45%'],
                'maxWidth': '100%',
                'position': 'relative',
                'width': '100%',
                '&::before': {
                  content: '""',
                  display: 'block',
                  pb: `calc(100% * ${
                    (property?.images?.[currentImage]?.asset?.gatsbyImageData
                      ?.height ?? 0) /
                    (property?.images?.[currentImage]?.asset?.gatsbyImageData
                      ?.width ?? 1)
                  })`,
                },
              }}
            >
              {transitions(({ opacity }, currentImage) => (
                <animated.div
                  style={{
                    opacity: opacity.to({ range: [0, 1], output: [0, 1] }),
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                  }}
                >
                  {property &&
                    property.images &&
                    property.images[currentImage]?.asset?.gatsbyImageData && (
                      <IntrinsicImage
                        alt={property.images[currentImage]?.alt ?? ''}
                        image={
                          property.images[currentImage]?.asset
                            ?.gatsbyImageData as IGatsbyImageData
                        }
                        ratio={496 / 718}
                      />
                    )}
                </animated.div>
              ))}
            </Box>
            <Flex
              sx={{
                flex: '0 0 auto',
                flexDirection: 'column',
                gap: [3, null, 4],
                maxWidth: '100%',
                width: ['100%', null, '30%'],
              }}
            >
              <Flex
                sx={{
                  flex: '0 0 auto',
                  flexDirection: 'column',
                  gap: 3,
                  maxWidth: '100%',
                  width: '100%',
                }}
              >
                {/* BUTTONS */}
                {property.ctas?.map(
                  (cta) =>
                    cta &&
                    cta.url && (
                      <Link
                        key={cta._key}
                        sx={{
                          textAlign: 'center',
                        }}
                        to={cta.url}
                        variant="button"
                      >
                        {cta.caption}
                      </Link>
                    ),
                )}
              </Flex>
              <Flex sx={{ flexWrap: 'wrap', gap: [3, null, 2] }}>
                {(property?.thumbnails?.length || 0) > 1
                  ? property?.thumbnails?.map((thumbnail, i) => (
                      <Button
                        key={thumbnail?._key}
                        onClick={() => setCurrentImage(i)}
                        sx={{
                          border: 'none',
                          flex: '0 0 auto',
                          maxWidth: '100%',
                          p: 0,
                          transform: `scale(${
                            currentImage === i ? '0.9' : '1'
                          })`,
                          transition: 'transform 0.15s',
                          width: (theme) => [
                            `calc((100% / 3) - (${theme.space?.[3]}px * (2 / 3)))`,
                            null,
                            `calc((100% / 2) - (${theme.space?.[2]}px * (1 / 2)))`,
                          ],
                        }}
                        type="button"
                      >
                        {thumbnail?.asset?.gatsbyImageData && (
                          <IntrinsicImage
                            alt={thumbnail?.alt ?? ''}
                            image={thumbnail.asset!.gatsbyImageData}
                            sx={{ width: '100%' }}
                          />
                        )}
                      </Button>
                    ))
                  : ''}
              </Flex>
            </Flex>
          </Flex>
          <Box>
            <Box sx={{ maxWidth: 700, mb: 4 }}>
              <SanityRichText
                blocks={property.copy as unknown as Block[]}
              ></SanityRichText>
            </Box>
            <AnchorSection id="attributes">
              <H
                sx={{
                  variant: 'text.smallTitle',
                  borderBottom: (theme) =>
                    `1px solid ${theme.colors?.backgroundOverlay}`,
                  mb: [3, null, 6],
                  pb: [2, null, 4],
                }}
              >
                {property.attributesSection?.title}
              </H>
              <Section sx={{ columnCount: [null, null, 2], columnGap: 6 }}>
                {property?.attributesSection?.attributes?.map((attribute) => (
                  <Flex
                    key={attribute?._key}
                    ref={(el) =>
                      attributeRefs.current.set(attribute?._key || '', el)
                    }
                    sx={{
                      borderBottom: (theme) =>
                        borderlessAttributes.has(attribute?._key || '')
                          ? 'none'
                          : `1px solid ${theme.colors?.backgroundOverlay}`,
                      boxSizing: 'content-box',
                      breakAfter: 'avoid',
                      breakInside: 'avoid',
                      flexDirection: 'column',
                      gap: [3, null, 4],
                      pb: [3, null, 4],
                      mb: [3, null, 4],
                    }}
                  >
                    <H
                      sx={{
                        variant: 'text.smallTitle',
                        fontSize: (theme) =>
                          `calc(${theme.fontSizes?.[2]}px + 1px)`,
                      }}
                    >
                      {attribute?.title}
                    </H>
                    <SanityRichText
                      blocks={attribute?.value as unknown as Block[]}
                      sx={{ 'p:last-child': { mb: 0 } }}
                    ></SanityRichText>
                  </Flex>
                ))}
              </Section>
            </AnchorSection>
          </Box>
        </Flex>
      </Section>
    </Layout>
  );
};

export default LeasingDetail;
